import React from 'react';
import styled from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { useForm } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// components

import { Modal } from 'app-components';
import GIButton from '@common-components/button/GIButton';

// props

interface ResetPasswordModalProps {
    hhId: string;
    onClose: () => void;
    onClickHandler: (hhid: any, data: FormValues) => void;
}

// types

type FormValues = {
    email: string;
    alternateEmail: string;
    reason: string;
};

// styles

const StyledModal = styled.div`
    display: flex;
    align-items: center;
    margin-top: 5px;
    margin-left: 20px;

    .radio-text {
        margin-left: 8px;
    }
`;

function ResetPasswordModal({ hhId, onClose, onClickHandler }: ResetPasswordModalProps) {
    const {
        register,
        handleSubmit,
        watch,
        setValue,
        clearErrors,
        formState: { errors },
    } = useForm<FormValues>({ defaultValues: {}, mode: 'onChange' });

    const intl = useIntl();

    const onClickSendHandler = async (data: FormValues) => {
        onClickHandler(hhId, data);
    };

    const btnDisabled = watch('email', '');
    const onCurrentEmailSelection = e => {
        setValue('email', e.target.value);
        clearErrors();
    };

    return (
        <form onSubmit={handleSubmit(onClickSendHandler)}>
            <Modal id="account_activation_email_modal">
                <Modal.Header
                    id="account_activation_email_modal_title"
                    titleId="reset_password.modal.header"
                    onHide={onClose}
                />
                <Modal.Body>
                    <label className="control-label required-true">
                        <FormattedMessage id="reset_password.modal.description" />
                    </label>
                    <label htmlFor="field-currentEmail">
                        <StyledModal>
                            <input
                                {...register('email')}
                                type="radio"
                                value={'hixEmail'}
                                onChange={onCurrentEmailSelection}
                                id="field-currentEmail"
                            />
                            <span className="radio-text">
                                {' '}
                                <FormattedMessage id="reset_password.modal.radio.currentEmail" />{' '}
                            </span>
                        </StyledModal>
                    </label>
                    <label htmlFor="field-alternateEmail">
                        <StyledModal>
                            <input
                                {...register('email')}
                                type="radio"
                                value={'alternateEmail'}
                                id="field-alternateEmail"
                            />
                            <span className="radio-text">
                                {' '}
                                <FormattedMessage id="reset_password.modal.radio.alternateEmail" />{' '}
                            </span>
                        </StyledModal>
                    </label>
                    {btnDisabled === 'alternateEmail' && (
                        <input
                            type="text"
                            className="ml-5"
                            {...register('alternateEmail', {
                                required: {
                                    value: true,
                                    message: intl.formatMessage({
                                        id: 'reset_password.modal.alternate.email.required',
                                    }),
                                },
                                pattern: {
                                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                    message: intl.formatMessage({
                                        id: 'reset_password.modal.alternate.email.required',
                                    }),
                                },
                            })}
                        />
                    )}

                    {btnDisabled === 'alternateEmail' && (
                        <div className="ml-5">
                            {errors?.alternateEmail && (
                                <p className="shown error--msg">
                                    <em>
                                        <FontAwesomeIcon icon="exclamation" />
                                    </em>
                                    {errors?.alternateEmail?.message}
                                </p>
                            )}
                        </div>
                    )}
                    {btnDisabled === 'alternateEmail' && (
                        <div className="mt-5">
                            <div className="control-label required-true">
                                <FormattedMessage id="reset_password.modal.alternate.email.reason" />
                            </div>
                            <input
                                type="text"
                                className="mt-4 control-label container"
                                {...register('reason', {
                                    required: {
                                        value: true,
                                        message: intl.formatMessage({
                                            id: 'reset_password.modal.alternate.email.reason.required',
                                        }),
                                    },
                                })}
                            />
                            {errors?.reason && (
                                <div className="shown error--msg">
                                    <em>
                                        <FontAwesomeIcon icon="exclamation" />
                                    </em>
                                    {errors?.reason?.message}{' '}
                                </div>
                            )}
                        </div>
                    )}

                    <p className="usa-alert usa-alert-info gi-alert gi-alert__info">
                        <FormattedMessage id="reset_password.modal.alert.message" />
                    </p>
                </Modal.Body>
                <Modal.Controls>
                    <GIButton
                        id="cancel"
                        labelId="common.cancel"
                        variant="secondary"
                        onClickHandler={onClose}
                    ></GIButton>
                    <GIButton
                        type={'submit'}
                        id="member_view"
                        labelId="button.submit"
                        variant="primary"
                        isDisabled={!btnDisabled}
                    ></GIButton>
                </Modal.Controls>
            </Modal>
        </form>
    );
}

export default ResetPasswordModal;
